import {useMutation, useQuery} from "react-query";
import axios from "axios";
import {getAuthConfig, getValidateConfig} from "../utils";
const auth = getAuthConfig()

export function PostAIAgent() {
    return useMutation('aiagentNew', async (postData) => {
        const { data } = await axios.post(
            "/api/v2/aiagent.php",
            postData,
            auth
        );
        return data;
    });
}

export function PostAIAgentWithId(agentId) {
    return useMutation('aiagent' + agentId, async (postData) => {
        const { data } = await axios.post(
            `/api/v2/aiagent.php/${agentId}`,
            postData,
            auth
        );
        return data;
    });
}

export function GetAIAgent(agentId, enabled = true) {
    return useQuery(
        "aiagent" + agentId,
        async () => {
            const { data } = await axios.get(
                `/api/v2/aiagent.php/${agentId}`,
                auth
            );
            return data;
        },
        {
            enabled: enabled && !!agentId
        }
    );
}

// --- Operation polling (for LROs like CreateRagCorpus) ---

export async function pollOperation(operationName, { interval = 2000, maxAttempts = 90 } = {}) {
    // operationName is full resource path e.g. "projects/.../operations/123"
    // Extract relative path: "operations/123"
    const relativePath = operationName.includes('/operations/')
        ? 'operations/' + operationName.split('/operations/').pop()
        : operationName;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const { data } = await axios.get(
            `/api/v2/gemini.php/${relativePath}`,
            getAuthConfig()
        );
        if (data.done) {
            return data;
        }
        await new Promise(resolve => setTimeout(resolve, interval));
    }
    throw new Error('Operation timed out');
}

// --- RAG Corpus Management (gemini.php passthrough) ---

export function CreateRagCorpus() {
    return useMutation('createRagCorpus', async (corpusData) => {
        const { data } = await axios.post(
            "/api/v2/gemini.php/ragCorpora",
            corpusData,
            auth
        );
        return data;
    });
}

export function GetRagCorpora() {
    return useQuery("ragCorpora", async () => {
        const { data } = await axios.get(
            "/api/v2/gemini.php/ragCorpora",
            auth
        );
        return data;
    });
}

export function DeleteRagCorpus() {
    return useMutation('deleteRagCorpus', async (corpusName) => {
        const { data } = await axios.delete(
            `/api/v2/gemini.php/${corpusName}`,
            auth
        );
        return data;
    });
}

// --- RAG Corpus File Management ---

export function ImportRagFile() {
    return useMutation('importRagFile', async ({ corpusName, fileData }) => {
        const { data } = await axios.post(
            `/api/v2/gemini.php/${corpusName}/ragFiles:import`,
            fileData,
            auth
        );
        return data;
    });
}

export function UploadRagFile() {
    return useMutation('uploadRagFile', async ({ ragCorpusId, file }) => {
        const formData = new FormData();
        formData.append('metadata', JSON.stringify({
            rag_file: {
                display_name: file.name,
                description: file.name
            }
        }));
        formData.append('file', file);
        const config = getValidateConfig();
        const { data } = await axios.post(
            `/api/v2/gemini.php/ragCorpora/${ragCorpusId}/ragFiles:upload`,
            formData,
            config
        );
        return data;
    });
}

export function GetRagCorpusFiles(corpusName, enabled = true) {
    return useQuery(
        ['ragCorpusFiles', corpusName],
        async () => {
            const { data } = await axios.get(
                `/api/v2/gemini.php/${corpusName}/ragFiles`,
                auth
            );
            return data;
        },
        {
            enabled: enabled && !!corpusName
        }
    );
}

export function DeleteRagFile() {
    return useMutation('deleteRagFile', async ({ corpusName, ragFileName }) => {
        const { data } = await axios.delete(
            `/api/v2/gemini.php/${corpusName}/ragFiles/${ragFileName}`,
            auth
        );
        return data;
    });
}

// --- Shared corpus creation helper ---

function extractCorpusId(resourceName) {
    if (!resourceName) return null;
    const parts = resourceName.split('/');
    return parts[parts.length - 1];
}

/**
 * Recovery path for when the create operation is slow to complete: the corpus
 * may already exist in GCP even though polling timed out. Look it up by display
 * name so we can still register its metadata instead of orphaning it.
 * Picks the most recently created match when several share a display name.
 */
async function findCorpusByDisplayName(displayName) {
    if (!displayName) return null;
    const { data } = await axios.get("/api/v2/gemini.php/ragCorpora", getAuthConfig());
    const matches = (data?.ragCorpora || []).filter(c => c.displayName === displayName);
    if (matches.length === 0) return null;
    matches.sort((a, b) => (b.createTime || '').localeCompare(a.createTime || ''));
    return matches[0];
}

/**
 * Creates a RAG corpus via LRO, polls until done, and registers it in the codes table.
 * If polling times out (slow GCP operation), falls back to looking the corpus up by
 * display name so its metadata is still saved and the corpus isn't orphaned.
 * Returns { corpusId, resourceName }.
 */
export async function createCorpusAndRegister({ title, corpusBody, createMutation, postCodesMutation }) {
    const createRes = await createMutation.mutateAsync(corpusBody);

    let corpusResource;
    try {
        const completedOp = await pollOperation(createRes.name);
        corpusResource = completedOp.response;
    } catch (pollErr) {
        // Operation didn't report done in time — it may still have succeeded.
        // Recover by finding the corpus we just asked to create by its display name.
        corpusResource = await findCorpusByDisplayName(corpusBody?.displayName);
        if (!corpusResource?.name) throw pollErr;
    }

    if (!corpusResource?.name) throw new Error('Corpus creation failed — no resource in operation response');

    const corpusId = extractCorpusId(corpusResource.name);
    const resourceName = corpusResource.name;

    await postCodesMutation.mutateAsync([{
        codeset: 'corpus',
        code: corpusId,
        value: {
            title,
            resourceName,
            category: 'knowledge',
            createdAt: new Date().toISOString()
        }
    }]);

    return { corpusId, resourceName };
}

// --- RAG Retrieval ---

export function RetrieveContexts() {
    return useMutation('retrieveContexts', async (requestBody) => {
        const { data } = await axios.post(
            "/api/v2/gemini.php/:retrieveContexts",
            requestBody,
            auth
        );
        return data;
    });
}

// --- Chat ---

export function GeminiGenerateContent() {
    return useMutation('geminiGenerateContent', async ({ model, requestBody }) => {
        const { data } = await axios.post(
            `/api/v2/gemini.php/publishers/google/models/${model}:generateContent`,
            requestBody,
            auth
        );
        return data;
    });
}
